import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import { UI } from '../constants';

function getFees(profitPercent) {
  return [
    {
      label: 'Transfer fee',
      value: String(profitPercent),
      suffix: '%',
      body: 'Applied once on what you send.',
    },
    {
      label: 'Rate spread',
      value: '0',
      suffix: '%',
      body: 'Mid-market, nothing added.',
    },
    {
      label: 'Payout fee',
      value: '$0',
      suffix: '',
      body: 'Recipient gets the full amount.',
    },
  ];
}

function renderSuffix(suffix) {
  if (!suffix) {
    return null;
  }

  return (
    <Typography
      component="span"
      sx={{
        fontSize: { xs: 22, md: 26 },
        fontWeight: 900,
        color: UI.green,
        letterSpacing: '-0.02em',
      }}
    >
      {suffix}
    </Typography>
  );
}

export default function FeesSection() {
  const profitPercent = Number(useSelector((state) => state?.settings?.profitPercent) ?? 0);
  const fees = useMemo(() => getFees(profitPercent), [profitPercent]);

  return (
    <Box
      id="fees"
      sx={{
        bgcolor: UI.bg,
        borderTop: `1px solid ${UI.border}`,
        borderBottom: `1px solid ${UI.border}`,
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          sx={{
            fontFamily: UI.mono,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: UI.green,
            mb: 2,
          }}
        >
          Pricing
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '2.4rem', md: '3.4rem' },
            fontWeight: 900,
            lineHeight: 1.02,
            letterSpacing: '-0.035em',
            color: UI.text,
          }}
        >
          One fee.
          <br />
          Mid-market rate.
        </Typography>

        <Typography
          sx={{
            mt: 3,
            fontSize: { xs: '1.05rem', md: '1.2rem' },
            lineHeight: 1.55,
            color: UI.textMuted,
            maxWidth: 620,
          }}
        >
          No spread baked into the rate, no receiving fee on the Somalia side.
        </Typography>

        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            columnGap: 5,
            rowGap: 4,
          }}
        >
          {fees.map((fee, index) => (
            <Box
              key={fee.label}
              sx={{
                pl: { xs: 0, md: index === 0 ? 0 : 5 },
                borderLeft: {
                  xs: 'none',
                  md: index === 0 ? 'none' : `0.5px solid ${UI.border}`,
                },
                pb: { xs: index === fees.length - 1 ? 0 : 3, md: 0 },
                borderBottom: {
                  xs: index === fees.length - 1 ? 'none' : `0.5px solid ${UI.border}`,
                  md: 'none',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', lineHeight: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: 40, md: 44 },
                    fontWeight: 900,
                    letterSpacing: '-0.03em',
                    color: UI.text,
                    lineHeight: 1,
                  }}
                >
                  {fee.value}
                </Typography>
                {renderSuffix(fee.suffix)}
              </Box>

              <Typography
                sx={{
                  mt: 1.25,
                  fontFamily: UI.mono,
                  fontSize: 11,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: UI.textFaint,
                }}
              >
                {fee.label}
              </Typography>

              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: 14,
                  color: UI.textMuted,
                  lineHeight: 1.6,
                }}
              >
                {fee.body}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            mt: 5,
            fontFamily: UI.mono,
            fontSize: 12,
            color: UI.textFaint,
          }}
        >
          Network fees charged by the blockchain are shown separately at confirmation.
        </Typography>
      </Container>
    </Box>
  );
}
